import React, { useEffect, useState } from 'react';

import {
  Grid,
  Typography,
  ClickAwayListener,
  IconButton,
  Link,
  Tooltip,
  useMediaQuery,
  Card,
  CardContent,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  Tooltip as ChartToolTip,
  LineChart,
  ResponsiveContainer,
  Line,
  XAxis,
  YAxis,
  ReferenceLine,
  CartesianGrid,
} from 'recharts';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockSharpIcon from '@mui/icons-material/LockSharp';
import ArrowRightAltOutlinedIcon from '@mui/icons-material/ArrowRightAltOutlined';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import TooltipCard from '../TooltipCard';
import InfoTooltip from '../../Tooltips/InfoTooltip';
import { infoIconStyle, cardTitle, infoIconButton } from 'src/App/Dashboards/Market/styles/style';
import {
  COLOR_PRIMARY_DARK,
  COLOR_TOOLTIP_BACKGROUND,
  COLOR_BLACK,
  COLOR_PRIMARY_LIGHT,
  exchangeRates,
  COLOR_E,
  COLOR_RED,
} from 'src/const';
import { FrontViewKeyMetricsPropTypes } from 'src/interfaces/iproptypes';
import { useHistory } from 'react-router-dom';
import { COLOR_GREEN } from 'src/const';
import {
  abbreviateNumber,
  calculateMoMChange,
  calculateRollingYoYChange,
  convertCurrency,
  formatCurrency,
  getSeasonality,
  getSeasonalityBarColor,
  getStartingEndingDate,
  isEmpty,
} from 'src/utils/functions';
import HistoricalModal from 'src/App/Modals/HistoricalModal';
import { useSelector } from 'react-redux';
import { Revenue, TempObject } from 'src/interfaces/idashboard';
import mixpanel from 'mixpanel-browser';
import { SelecFilterChip } from 'src/App/Components/Chips';
import {
  COLOR_ERROR_1,
  COLOR_ERROR_BACKGROUNG,
  COLOR_PRIMARY_2,
  COLOR_SECONDARY_2_100,
  COLOR_SECONDARY_2_800,
  COLOR_SUCCESS_1,
  COLOR_SUCCESS_BACKGROUNG,
} from 'src/colors';

const useStyles = makeStyles((theme: Theme) => ({
  linearProgress: {
    width: '100%',
    marginBottom: 20,
    color: COLOR_PRIMARY_DARK,
  },
}));

const loadingStyle = makeStyles((theme: Theme) => ({
  colorPrimary: {
    backgroundColor: (props: any) => props.backgroundColor,
  },
  barColorPrimary: {
    backgroundColor: COLOR_TOOLTIP_BACKGROUND,
  },
}));

const FrontViewKeyMetrics = ({
  title,
  value,
  tooltip,
  color,
  isDisabled,
  handleOnSampleClick,
  flipCard,
  hasLastMonthData,
  historicalDataLink,
  unit,
  lastMonthData,
  currency,
  multipleMarkersCallLoading,
  renderIn,
  isUserAllowed,
}: FrontViewKeyMetricsPropTypes) => {
  const classes = useStyles();
  const { colorPrimary, barColorPrimary } = loadingStyle({ backgroundColor: color });
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [metricsKey, setMetricsKey] = useState('');
  const [historicalMonthlyData, setHistoricalMonthlyData] = useState([]);
  const [data, setData] = useState([] as TempObject[]);
  const [metrics, setMetrics] = useState('');
  const [openHistoricalModal, setOpenHistoricalModal] = useState<boolean>(false);
  const [historicalChartTitle, setHistoricalChartTitle] = useState<string>('');
  const [momChange, setMomChange] = useState(0);
  const [yoyChange, setYoyChange] = useState(0);
  const history = useHistory();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const matches = useMediaQuery(theme.breakpoints.down(900));
  const [showGraph, setShowGraph] = useState(false);
  const historicalData = useSelector(state => (state as any).dashboard.historicalData);
  const [seasonalityData, setSeasonalityData] = useState(
    {} as {
      highestRevenue: Revenue;
      lowestRevenue: Revenue;
      seasonality: number;
    }
  );
  mixpanel.init('0c9d3451de8e0a1a2f21ec23b05c2063');
  const yAxisName = title.includes('Revenue') ? 'Revenue' : title;

  const hoverGraph = () => {
    mixpanel.track('checkTooltipOnInvestmentDashboard');
  };

  const search = useSelector(state => (state as any).dashboard.search.data);

  useEffect(() => {
    if (title.includes('Revenue')) {
      setMetricsKey('revenue');
      setMetrics('Revenue');
    } else if (title.includes('Occupancy')) {
      setMetricsKey('occupancy_rate');
      setMetrics('Occupancy Rate');
    } else if (title.includes('Nightly')) {
      setMetricsKey('daily_rate');
      setMetrics('Nightly Rate');
    }
  }, []);

  useEffect(() => {
    setHistoricalMonthlyData(historicalData);
  }, [historicalData]);

  useEffect(() => {
    if (historicalMonthlyData) {
      let keys = Object.keys(historicalMonthlyData);
      let tempData: TempObject[] = [];

      for (let i = 0; i < keys.length; i++) {
        let val = historicalMonthlyData[keys[i]]?.[metricsKey + '50'];

        let tempObj = {
          '0.5': !val
            ? 'missing'
            : metricsKey != 'occupancy_rate'
            ? convertCurrency(currency, val)
            : val,
          '0.9': 0,
          '0.25': 0,
          '0.75': 0,
          [metrics]: !val
            ? 'missing'
            : metricsKey != 'occupancy_rate'
            ? convertCurrency(currency, val)
            : val,
          timePeriod: formatDate(keys[i]),
          date: keys[i],
        };
        tempData.push(tempObj);
      }
      setData(tempData);
      setShowGraph(true);
    }
  }, [historicalMonthlyData, currency]);

  useEffect(() => {
    if (title.includes('Revenue') && data.length > 0) {
      let seasonalityData = getSeasonality(data.slice(-12));
      setSeasonalityData(seasonalityData);
    }
    if (data.length > 0) {
      const momChange = calculateMoMChange(data, metrics);
      setMomChange(momChange as number);
      const yoyChange = calculateRollingYoYChange(data, metrics);
      setYoyChange(yoyChange as number);
    }
  }, [data]);

  function formatDate(dateString) {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const [year, month] = dateString.split('-');
    const monthName = months[parseInt(month, 10) - 1];

    return `${monthName} ${year}`;
  }

  // ----------
  const formatValue = (rawKeyMetricsVal: number, updateCurrency = true) => {
    let keyMetricsVal = '';
    if (rawKeyMetricsVal == -1) {
      return title.includes('Occupancy') ? '-' + unit : unit + '-';
    }

    if (isDisabled && lastMonthData && isNaN(parseInt(lastMonthData))) {
      return title.includes('Occupancy') ? '-' + unit : unit + '-';
    }

    if (title.includes('Occupancy') || !updateCurrency) {
      keyMetricsVal = rawKeyMetricsVal.toFixed(0);
    } else {
      keyMetricsVal = convertCurrency(currency, rawKeyMetricsVal).toFixed(0);
    }

    const formattedVal = title.includes('Occupancy')
      ? keyMetricsVal
      : formatCurrency(keyMetricsVal);
    return title.includes('Occupancy') ? formattedVal + unit : unit + formattedVal;
  };

  // Logic for Dynamic data updation
  const [valueChange, setValueChange] = useState(0);
  const [prevValue, setPrevValue] = useState(-1);
  useEffect(() => {
    if (!multipleMarkersCallLoading) {
      if (prevValue != -1) {
        let tempPrevValue = prevValue;
        let tempValue = value;
        if (!title.includes('Occupancy')) {
          tempPrevValue = convertCurrency(currency, prevValue);
          tempValue = convertCurrency(currency, value);
        }
        setValueChange(tempValue - tempPrevValue);
      }
      setPrevValue(value);
    }
  }, [value, multipleMarkersCallLoading]);
  // -----------

  const CustomTooltip = ({ active, payload, label, classes, percentile }) => {
    if (active && payload && payload.length > 0) {
      const _payload = payload[0].payload;
      if (
        _payload.Revenue === 'missing' ||
        _payload['Nightly Rate'] === 'missing' ||
        _payload['Occupancy Rate'] === 'missing'
      ) {
        return (
          <Card>
            <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
              <Typography variant="body1" style={{ fontWeight: 'bolder' }}>
                {_payload.timePeriod}
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                Data Unavailable
              </Typography>
            </CardContent>
          </Card>
        );
      } else if (
        _payload.Revenue === 'inactive' ||
        _payload['Nightly Rate'] === 'inactive' ||
        _payload['Occupancy Rate'] === 'inactive'
      ) {
        return (
          <Card>
            <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
              <Typography variant="body1" style={{ fontWeight: 'bolder' }}>
                {_payload.timePeriod}
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                Inactive
              </Typography>
            </CardContent>
          </Card>
        );
      } else {
        return (
          <Card>
            <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
              <Typography variant="body1" style={{ fontWeight: 'bolder' }}>
                {payload[0].payload.timePeriod}
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                {/* <span style={{ color: percentile === 0.9 ? COLOR_GREEN : 'black' }}>
                  90th percentile {payload[0].name}: {payload[0].payload['0.9']}
                  {payload[0].unit}
                </span>
                <br />
                <span style={{ color: percentile === 0.75 ? COLOR_GREEN : 'black' }}
                  75th percentile {payload[0].name}: {payload[0].payload['0.75']}
                  {payload[0].unit}
                </span>
                <br /> */}
                <span style={{ color: percentile === 0.5 ? COLOR_GREEN : 'black' }}>
                  Median {payload[0].name}: {payload[0].name != 'Occupancy Rate' && payload[0].unit}
                  {payload[0].payload['0.5']}
                  {payload[0].name == 'Occupancy Rate' && payload[0].unit}
                </span>
                <br />
                {/* <span style={{ color: percentile === 0.25 ? COLOR_GREEN : 'black' }}>
                  25th Percentile {payload[0].name}: {payload[0].payload['0.25']}
                  {payload[0].unit}
                </span> */}
              </Typography>
            </CardContent>
          </Card>
        );
      }
    }

    return null;
  };

  const CustomizedDot = props => {
    //linechart
    const { cx, cy, value, color, getHighLowPoints } = props;

    const limit = getHighLowPoints();

    const size = 6;
    const smallSize = 4;

    if (value === limit.high || value === limit.low) {
      return (
        <svg x={cx - size} y={cy - size} width={size * 2} height={size * 2} fill="white">
          <g transform={`translate(${size} ${size})`}>
            <circle r={size} fill={color} />
            {/* <circle r={size / 2} fill="white" /> */}
          </g>
        </svg>
      );
    } else if (value !== null && value >= 0) {
      return (
        <svg
          x={cx - smallSize}
          y={cy - smallSize}
          width={smallSize * 2}
          height={smallSize * 2}
          fill="white"
        >
          <g transform={`translate(${smallSize} ${smallSize})`}>
            <circle r={smallSize} fill={color} />
            <circle r={smallSize / 2} fill="white" />
          </g>
        </svg>
      );
    } else {
      return (
        <svg
          x={cx - smallSize}
          y={cy - smallSize}
          width={smallSize * 2}
          height={smallSize * 2}
          fill="white"
        >
          <g transform={`translate(${smallSize} ${smallSize})`}>
            <circle r={smallSize} fill={'#0000'} />
          </g>
        </svg>
      );
    }
  };

  const getHighLowPoints = () => {
    let low = Infinity;
    let high = -Infinity;
    data.forEach(data => {
      if (data[`${metricsKey}+50`] != 'missing' && data[`${metricsKey}+50`] != 'inactive') {
        if (high < data[`${metricsKey}+50`]) {
          high = data[`${metricsKey}+50`];
        }
        if (low > data[`${metricsKey}+50`]) {
          low = data[`${metricsKey}+50`];
        }
      }
    });
    return { high, low };
  };

  const combineFormatterYAxis = val => {
    if (isFinite(val)) {
      if (title.includes('Revenue')) {
        return abbreviateNumber(val);
      } else {
        return abbreviateNumber(val, false);
      }
    } else {
      return `$${val}`;
    }
  };

  return (
    <Grid container direction="row" sx={{ height: isSmallScreen ? 190 : 170, background: '#fff' }}>
      {renderIn == 'insights' && !matches && (
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          item
          xs={10}
          sx={{ padding: '16px', minWidth: '100%' }}
        >
          <Grid container direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h2" style={cardTitle}>
              {title}
              <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
                <InfoTooltip
                  title={
                    <TooltipCard
                      title={tooltip.title}
                      content={tooltip.content[0]}
                      contentAdditional={tooltip.content.length > 1 ? tooltip.content[1] : ''}
                      contentAdditional2={tooltip.content.length > 2 ? tooltip.content[2] : ''}
                      link=""
                    />
                  }
                  enterTouchDelay={0}
                  leaveTouchDelay={6000}
                  open={isTooltipOpen}
                  onOpen={() => setIsTooltipOpen(true)}
                  onClose={() => setIsTooltipOpen(false)}
                >
                  <IconButton
                    style={infoIconButton}
                    onClick={() => setIsTooltipOpen(prev => !prev)}
                  >
                    <InfoOutlinedIcon style={infoIconStyle} />
                  </IconButton>
                </InfoTooltip>
              </ClickAwayListener>
            </Typography>

            {showGraph && (
              <div>
                <Typography sx={{ fontSize: '12px', paddingRight: '5px', fontWeight: 800 }}>
                  {data[data.length - 12]?.timePeriod} - {data[data.length - 1]?.timePeriod}
                </Typography>
              </div>
            )}
          </Grid>

          <Grid container sx={{ height: '50%', width: '100%' }}>
            <Grid item xs={5}>
              {isDisabled && !title.includes('Nightly Rate') && !hasLastMonthData ? (
                <IconButton onClick={handleOnSampleClick}>
                  <LockSharpIcon style={{ fontSize: 40, color: '#4aa59a' }} />
                </IconButton>
              ) : (
                <>
                  {title.includes('Revenue') ? (
                    <Tooltip title={formatValue(value)}>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          color: color,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                        }}
                        variant={isSmallScreen ? 'h6' : 'h4'}
                        component="h3"
                      >
                        {formatValue(value)}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography
                      sx={{
                        fontWeight: 900,
                        color: color,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                      variant={isSmallScreen ? 'h6' : 'h4'}
                      component="h3"
                    >
                      {formatValue(value)}
                    </Typography>
                  )}
                  <div style={{ margin: '10px 0px' }}>
                    <SelecFilterChip
                      height="15px"
                      width="100px"
                      borderRadius="15px"
                      colors={{
                        backgroundColor:
                          momChange > 0 ? COLOR_SUCCESS_BACKGROUNG : COLOR_ERROR_BACKGROUNG,
                        color: momChange > 0 ? COLOR_SUCCESS_1 : COLOR_ERROR_1,
                        borderColor:
                          momChange > 0 ? COLOR_SUCCESS_BACKGROUNG : COLOR_ERROR_BACKGROUNG,
                      }}
                      text={`MoM Change: ${
                        momChange > 0
                          ? `+${momChange}%↑`
                          : `${momChange}%${momChange >= 0 ? '↑' : '↓'}`
                      }`}
                      fontWeight="bold"
                      handleOnClick={() => {}}
                      isSelected={false}
                      tooltip=""
                      fontSize="10px"
                    />
                  </div>
                  {/* <SelecFilterChip
                    height="15px"
                    width="100px"
                    borderRadius="15px"
                    colors={{
                      backgroundColor:
                        yoyChange > 0 ? COLOR_SUCCESS_BACKGROUNG : COLOR_ERROR_BACKGROUNG,
                      color: yoyChange > 0 ? COLOR_SUCCESS_1 : COLOR_ERROR_1,
                      borderColor:
                        yoyChange > 0 ? COLOR_SUCCESS_BACKGROUNG : COLOR_ERROR_BACKGROUNG,
                    }}
                    text={`YoY Change: ${
                      yoyChange > 0
                        ? `+${yoyChange}%↑`
                        : `${yoyChange}%${yoyChange >= 0 ? '↑' : '↓'}`
                    }`}
                    fontWeight="bold"
                    handleOnClick={() => {}}
                    isSelected={false}
                    tooltip=""
                    fontSize="10px"
                  /> */}
                </>
              )}
            </Grid>
            <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'center' }}>
              {title.includes('Revenue') && !isDisabled && !isEmpty(seasonalityData) && (
                <Grid item xs={1} sx={{ height: '100%', width: '100%' }}>
                  <InfoTooltip
                    title={
                      <TooltipCard
                        title={`Seasonality: ${seasonalityData.seasonality}%`}
                        content={`Highest Revenue: ${unit}${formatCurrency(
                          String(
                            convertCurrency(
                              currency,
                              seasonalityData.highestRevenue?.revenue as number
                            )
                          )
                        )} (${seasonalityData.highestRevenue?.date})`}
                        contentAdditional={`Lowest Revenue: ${unit}${formatCurrency(
                          String(
                            convertCurrency(
                              currency,
                              seasonalityData.lowestRevenue?.revenue as number
                            )
                          )
                        )} (${seasonalityData.lowestRevenue?.date})`}
                      />
                    }
                  >
                    <div
                      style={{
                        width: 10,
                        borderRadius: 10,
                        height: `${seasonalityData.seasonality}%`,
                        background: getSeasonalityBarColor(seasonalityData.seasonality),
                      }}
                    ></div>
                  </InfoTooltip>
                </Grid>
              )}
            </Grid>
            <Grid item xs={6} alignItems={'center'} justifyContent={'center'}>
              {showGraph && data.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={data.slice(-12)}
                    // style={blurGraph}
                    onMouseEnter={hoverGraph}
                    margin={{
                      top: 10,
                      right: 10,
                      left: 0,
                      bottom: 10,
                    }}
                  >
                    <CartesianGrid strokeDasharray="5 5" vertical={false} />
                    <Line
                      isAnimationActive={false}
                      type="monotone"
                      dataKey={metrics}
                      stroke={color}
                      strokeWidth={3}
                      unit={unit}
                      dot={<CustomizedDot color={color} getHighLowPoints={getHighLowPoints} />}
                    />

                    <XAxis dataKey="timePeriod" hide={true} />
                    <YAxis
                      dataKey={metrics}
                      axisLine={false}
                      tickCount={5}
                      tickLine={false}
                      style={{
                        fontSize: '0.75rem',
                        fontFamily: 'Roboto',
                      }}
                      domain={title.includes('Occupancy') && [0, 100]}
                      label={{
                        value: title.includes('Occupancy') ? '%' : exchangeRates[currency].symbol,
                        position: 'insideLeft',
                        textAnchor: 'middle',
                        style: { fontSize: '0.75rem', fontFamily: 'Roboto', color: '#000000DE' },
                      }}
                      tickFormatter={combineFormatterYAxis}
                    />
                    <ReferenceLine
                      x={
                        data.length > 0
                          ? getStartingEndingDate(data[data.length - 12]?.date, true)
                          : ''
                      }
                      stroke={COLOR_PRIMARY_LIGHT}
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeDasharray="4 12"
                    />
                    <ReferenceLine
                      x={
                        data.length > 0
                          ? getStartingEndingDate(data[data.length - 1]?.date, true)
                          : ''
                      }
                      stroke={COLOR_PRIMARY_LIGHT}
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeDasharray="4 12"
                    />
                    <ChartToolTip
                      position={{ x: 0, y: 60 }}
                      content={
                        <CustomTooltip classes={classes} active payload label percentile={0.5} />
                      }
                      wrapperStyle={{ zIndex: 100000 }}
                      filterNull={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <CircularProgress sx={{ color: color }} />
                </div>
              )}
              {!isUserAllowed && (
                <div
                  style={{
                    position: 'relative',
                    height: '100%',
                    width: '100%',
                    backgroundColor: 'white',
                    opacity: 0.8,
                    top: '-100%',
                  }}
                >
                  <Typography
                    sx={{
                      position: 'absolute',
                      fontSize: '26px',
                      fontWeight: 900,
                      color: COLOR_E,
                      transform: 'translate(-50%, -50%) rotate(-30deg)',
                      cursor: 'pointer',
                      top: '40px',
                      left: '95px',
                    }}
                    onClick={() => {
                      handleOnSampleClick();
                    }}
                  >
                    SAMPLE
                  </Typography>
                </div>
              )}
            </Grid>
          </Grid>

          <Link
            sx={{
              color: COLOR_BLACK,
              textDecoration: 'none',
              ':hover': {
                textDecoration: 'underline',
              },
              marginTop: '10px',
              cursor: 'pointer',
            }}
            onClick={() => {
              if (historicalDataLink) {
                setOpenHistoricalModal(true);
                setHistoricalChartTitle(title);
              } else {
                isDisabled ? handleOnSampleClick() : flipCard('rear');
              }
            }}
          >
            <Tooltip title="See historical data">
              <Typography
                variant={isSmallScreen ? 'caption' : 'body2'}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: COLOR_PRIMARY_2,
                  textDecoration: 'underline',
                }}
              >
                View historical trends{' '}
                {isDisabled && (
                  <LockSharpIcon sx={{ color: COLOR_PRIMARY_LIGHT, fontSize: '20px' }} />
                )}{' '}
                <ArrowRightAltOutlinedIcon sx={{ fontSize: '25px' }} />
              </Typography>
            </Tooltip>
          </Link>
        </Grid>
      )}
      {(renderIn == 'estimator' || (renderIn == 'insights' && matches)) && (
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          alignItems="flex-start"
          item
          xs={10}
          sx={{ padding: '16px', paddingRight: 0 }}
        >
          <Grid container direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h2" style={cardTitle}>
              {title}
              <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
                <InfoTooltip
                  title={
                    <TooltipCard
                      title={tooltip.title}
                      content={tooltip.content[0]}
                      contentAdditional={tooltip.content.length > 1 ? tooltip.content[1] : ''}
                      contentAdditional2={tooltip.content.length > 2 ? tooltip.content[2] : ''}
                      link=""
                    />
                  }
                  enterTouchDelay={0}
                  leaveTouchDelay={6000}
                  open={isTooltipOpen}
                  onOpen={() => setIsTooltipOpen(true)}
                  onClose={() => setIsTooltipOpen(false)}
                >
                  <IconButton
                    style={infoIconButton}
                    onClick={() => setIsTooltipOpen(prev => !prev)}
                  >
                    <InfoOutlinedIcon style={infoIconStyle} />
                  </IconButton>
                </InfoTooltip>
              </ClickAwayListener>
            </Typography>

            {/* Code for dynamic value updation */}

            {/* {!multipleMarkersCallLoading && valueChange != 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                color: valueChange > 0 ? COLOR_GREEN : COLOR_RED,
              }}
            >
              <h6 style={{ fontWeight: 700 }}>{formatValue(valueChange, false)}</h6>
              {valueChange > 0 ? (
                <ArrowDropUpIcon color="success" fontSize="large" />
              ) : (
                <ArrowDropDownIcon color="error" fontSize="large" />
              )}
            </div>
          )} */}
          </Grid>

          {isDisabled && !title.includes('Nightly Rate') && !hasLastMonthData ? (
            <IconButton onClick={handleOnSampleClick}>
              <LockSharpIcon style={{ fontSize: 40, color: '#4aa59a' }} />
            </IconButton>
          ) : (
            <Typography
              sx={{ fontWeight: 900, color: color }}
              variant={isSmallScreen ? 'h6' : 'h4'}
              component="h3"
            >
              {formatValue(value)}
            </Typography>
          )}

          <Link
            sx={{
              color: COLOR_BLACK,
              textDecoration: 'none',
              ':hover': {
                textDecoration: 'underline',
              },
              cursor: 'pointer',
            }}
            onClick={() => {
              if (historicalDataLink) {
                setOpenHistoricalModal(true);
                setHistoricalChartTitle(title);
              } else {
                isDisabled ? handleOnSampleClick() : flipCard('rear');
              }
            }}
          >
            <Tooltip title="See historical data">
              <Typography
                variant={isSmallScreen ? 'caption' : 'body2'}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                View historical trends{' '}
                {isDisabled && (
                  <LockSharpIcon sx={{ color: COLOR_PRIMARY_LIGHT, fontSize: '20px' }} />
                )}{' '}
                <ArrowRightAltOutlinedIcon sx={{ fontSize: '25px' }} />
              </Typography>
            </Tooltip>
          </Link>
        </Grid>
      )}
      {openHistoricalModal ? (
        <HistoricalModal
          open={openHistoricalModal}
          historicalChartTitle={historicalChartTitle}
          onClose={() => setOpenHistoricalModal(false)}
          currency={currency}
          handleOnSampleClick={handleOnSampleClick}
        />
      ) : (
        ''
      )}
    </Grid>
  );
};

export default FrontViewKeyMetrics;
